import { useState, useEffect, useRef } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import styled from '@emotion/styled';
import { FiEye, FiEyeOff, FiCheckCircle, FiCircle } from 'react-icons/fi';
import { PasswordStrengthBar, calcScore } from '../../../../components/strength-bar/strength-bar';
import { useGlobalNotifications } from '../../../../hooks/use-global-notifications';

import {
  Title,
  SubTitle,
  MutedSubTitle,
  HelperText,
  PasskeyText,
  DeviceName,
  DateText,
  MutedText,
  ActionText,
  DeleteAccountButton,
  Panel,
  FormGrid,
  Block,
  RowBetween,
  Stack,
  AlertOverlay,
  AlertContentShell,
  CancelTextButton,
} from './account-settings.styled';
import ToggleSwitch from '../../../../components/toggle/toggle';
import { OnboardBtn } from '../profile/profile.styled';
import {
  Input,
  InputWithToggle,
  InputWrap,
  EyeToggle,
  Label,
} from './change-password/change-password.styled';
import { VerificationCodeInput } from '../../../../components/verification-code-input/verification-code-input';
import { usePasskeys } from '../../../../hooks/use-passkeys';
import { useUserSettings } from '../../../../hooks/use-user-settings';
import { api, profileApi, authApi, clearSession } from '../../../../api';
import { startAuthentication } from '@simplewebauthn/browser';
import {
  isNativeBiometricAvailable,
  promptNativeBiometric,
} from '../../../../helpers/native-biometric';
import { useAuth } from '../../../../contexts/auth/auth-context';
import { FloatingInput } from '../../../../components/floating-input/floating-input';
import {
  Prefix,
  Flag,
  PrefixText,
  DropdownContainer,
  DropdownMenu,
  DropdownOption,
  OptionLeft,
  OptionFlag,
  OptionName,
  OptionCode,
  CheckboxRow,
  CheckboxInput,
  CheckboxLabel,
} from '../../../registration/pages/number-verification/number-verification.styled';
import { sendCode, checkCode } from '../../../../helpers/sms-functions';
import { isNative } from '../../../../platform';

/* ── Phone helpers (same as registration) ── */
interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
  maxDigits: number;
}

const COUNTRIES: Country[] = [
  { code: 'US', name: 'United States', dialCode: '+1', flag: '/us-flag.png', maxDigits: 10 },
  { code: 'UK', name: 'United Kingdom', dialCode: '+44', flag: '/uk-flag.svg', maxDigits: 10 },
];

const digitsOnly = (raw: string) => raw.replace(/\D/g, '');

const digitsForStorage = (raw: string, country: Country) => {
  const hasPlus = raw.trim().startsWith('+');
  const digits = digitsOnly(raw);
  const cc = country.dialCode.replace(/\D/g, '');
  if (hasPlus && digits.startsWith(cc))
    return digits.slice(cc.length, cc.length + country.maxDigits);
  if (digits.length > country.maxDigits && digits.startsWith(cc))
    return digits.slice(cc.length, cc.length + country.maxDigits);
  return digits.slice(0, country.maxDigits);
};

const digitsForDisplay = (raw: string, country: Country, stripCC: boolean) => {
  const digits = digitsOnly(raw);
  const cc = country.dialCode.replace(/\D/g, '');
  if (stripCC && digits.length > country.maxDigits && digits.startsWith(cc))
    return digits.slice(cc.length, cc.length + country.maxDigits);
  return digits.slice(0, country.maxDigits);
};

const formatUSPhone = (raw: string, stripCC: boolean) => {
  const d = digitsForDisplay(raw, COUNTRIES[0], stripCC);
  const m = d.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
  if (!m) return '';
  let f = '';
  if (m[1]) f = `(${m[1]}`;
  if (m[1].length === 3) f += ') ';
  if (m[2]) f += m[2];
  if (m[2].length === 3) f += '-';
  if (m[3]) f += m[3];
  return f;
};

const formatUKPhone = (raw: string, stripCC: boolean) => {
  const d = digitsForDisplay(raw, COUNTRIES[1], stripCC);
  const m = d.match(/^(\d{0,4})(\d{0,6})$/);
  if (!m) return '';
  let f = '';
  if (m[1]) f = m[1];
  if (m[1].length === 4 && m[2]) f += ' ';
  if (m[2]) f += m[2];
  return f;
};

const formatPhone = (raw: string, country: Country, stripCC: boolean) => {
  if (country.code === 'US') return formatUSPhone(raw, stripCC);
  if (country.code === 'UK') return formatUKPhone(raw, stripCC);
  return digitsForDisplay(raw, country, stripCC);
};

/* Optional spacer primitives to replace <div style={{paddingTop: …}}/> */
export const VSpace = styled.div<{ $h?: number }>`
  height: ${({ $h = 16 }) => `${$h}px`};
`;

/* Device row */
const DeviceRow = ({
  name,
  authType,
  added,
  lastUsed,
  onRemove,
}: {
  name: string;
  authType?: string | null | undefined;
  added: string;
  lastUsed?: string | null;
  onRemove?: () => void;
}) => (
  <RowBetween role="group" aria-label={`${name} biometric credential`}>
    <Stack $gap={2}>
      <DeviceName>{name}</DeviceName>
      <DateText>
        {authType || 'Biometric'}
        {` \u00B7 Added ${added}`}
        {lastUsed && ` \u00B7 Last used ${lastUsed}`}
      </DateText>
    </Stack>
    <ActionText type="button" onClick={onRemove} aria-label={`Remove ${name} biometric credential`}>
      Remove
    </ActionText>
  </RowBetween>
);

const ThisDeviceAutoPrompt = ({
  autoPromptEnabled,
  onToggle,
  hasPasskeys,
  onAddPasskey,
}: {
  autoPromptEnabled: boolean;
  onToggle: (enabled: boolean) => void;
  hasPasskeys: boolean;
  onAddPasskey: () => void;
}) => (
  <RowBetween style={{ paddingBottom: 16 }}>
    <Stack $gap={2} aria-live="polite">
      <SubTitle>Auto-prompt biometric login</SubTitle>
      <MutedText>Automatically prompt for biometric login on this device.</MutedText>
    </Stack>
    <ToggleSwitch
      checked={hasPasskeys ? autoPromptEnabled : false}
      onChange={(enabled) => {
        if (!hasPasskeys) {
          onAddPasskey();
        } else {
          onToggle(enabled);
        }
      }}
    />
  </RowBetween>
);

type AccountSettingsProps = {
  /** Called when the user clicks "Update" to change password */
  onChangePasswordRequested?: () => void;
  /** Initial passkeys data */
  initialPasskeys?: any[];
  /** Initial auto prompt passkey setting */
  initialAutoPromptPasskey?: boolean;
};

export const AccountSettings = ({
  onChangePasswordRequested,
  initialPasskeys = [],
  initialAutoPromptPasskey = true,
}: AccountSettingsProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deviceToRemove, setDeviceToRemove] = useState<{ id: string; name: string } | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmIdentityOpen, setConfirmIdentityOpen] = useState(false);
  const [confirmIdentityAction, setConfirmIdentityAction] = useState<'email' | 'phone' | null>(
    null
  );
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [verifyEmailOpen, setVerifyEmailOpen] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyCodeStatus, setVerifyCodeStatus] = useState<'idle' | 'error' | 'success'>('idle');
  const [emailUpdatedOpen, setEmailUpdatedOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const resendTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // Forgot password flow
  const [forgotVerifyOpen, setForgotVerifyOpen] = useState(false);
  const [forgotCode, setForgotCode] = useState('');
  const [forgotCodeStatus, setForgotCodeStatus] = useState<'idle' | 'error' | 'success'>('idle');
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [forgotSending, setForgotSending] = useState(false);

  // Phone change state
  const [changePhoneOpen, setChangePhoneOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country>(COUNTRIES[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDropdownClosing, setIsDropdownClosing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [phonePretty, setPhonePretty] = useState('');
  const [agreeSms, setAgreeSms] = useState(false);
  const [verifyPhoneOpen, setVerifyPhoneOpen] = useState(false);
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneCodeStatus, setPhoneCodeStatus] = useState<'idle' | 'error' | 'success'>('idle');
  const [phoneUpdatedOpen, setPhoneUpdatedOpen] = useState(false);

  const phoneDigits = digitsForStorage(phonePretty, selectedCountry);
  const isPhoneValid = phoneDigits.length === selectedCountry.maxDigits;
  const fullPhoneNumber = `${selectedCountry.dialCode}${phoneDigits}`;

  const { devices, loading, error, addPasskey, removePasskey } = usePasskeys({ initialPasskeys });
  const { settings, updateAutoPromptPasskey } = useUserSettings({ initialAutoPromptPasskey });
  const { showError, showSuccess } = useGlobalNotifications();
  const { email: authEmail, profile, syncSession, signIn } = useAuth();
  const userEmail = profile?.email_address || authEmail || '';

  // Biometric availability probe (web only — native checks inside handleSetup)
  const [biometricAvailable, setBiometricAvailable] = useState<boolean | null>(null);
  useEffect(() => {
    if (isNative()) {
      setBiometricAvailable(true); // native checks at enrollment time
      return;
    }
    if (
      'PublicKeyCredential' in window &&
      typeof (PublicKeyCredential as any).isUserVerifyingPlatformAuthenticatorAvailable ===
        'function'
    ) {
      (PublicKeyCredential as any)
        .isUserVerifyingPlatformAuthenticatorAvailable()
        .then((v: boolean) => setBiometricAvailable(v))
        .catch(() => setBiometricAvailable(null));
    } else {
      setBiometricAvailable(false);
    }
  }, []);

  // Resend countdown cleanup
  useEffect(() => {
    return () => {
      if (resendTimer.current) clearInterval(resendTimer.current);
    };
  }, []);

  const startResendCooldown = () => {
    setResendSeconds(30);
    if (resendTimer.current) clearInterval(resendTimer.current);
    resendTimer.current = setInterval(() => {
      setResendSeconds((s) => {
        if (s <= 1) {
          if (resendTimer.current) clearInterval(resendTimer.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  // Phone dropdown handlers
  const toggleDropdown = () => {
    if (isDropdownOpen) {
      setIsDropdownClosing(true);
      setTimeout(() => {
        setIsDropdownOpen(false);
        setIsDropdownClosing(false);
      }, 200);
    } else {
      setIsDropdownOpen(true);
    }
  };

  const selectCountry = (country: Country) => {
    setSelectedCountry(country);
    const currentDigits = digitsForStorage(phonePretty, selectedCountry);
    setPhonePretty(formatPhone(currentDigits, country, false));
    setIsDropdownClosing(true);
    setTimeout(() => {
      setIsDropdownOpen(false);
      setIsDropdownClosing(false);
    }, 200);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        isDropdownOpen
      ) {
        setIsDropdownClosing(true);
        setTimeout(() => {
          setIsDropdownOpen(false);
          setIsDropdownClosing(false);
        }, 200);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    const prevDigits = digitsOnly(phonePretty);
    const nextDigits = digitsOnly(nextValue);
    const isDeleting = nextValue.length < phonePretty.length;
    if (isDeleting) {
      setPhonePretty(nextValue);
    } else {
      const likelyPaste = nextDigits.length - prevDigits.length > 1;
      setPhonePretty(formatPhone(nextValue, selectedCountry, likelyPaste));
    }
    setErrorMsg(null);
  };

  const handleBiometricVerify = async () => {
    setErrorMsg(null);
    setSaving(true);
    try {
      if (isNative()) {
        const nativeBio = await isNativeBiometricAvailable();
        if (!nativeBio.available) {
          setErrorMsg('Biometrics not available on this device.');
          setSaving(false);
          return;
        }
        await promptNativeBiometric('Confirm your identity');
      } else {
        // Web WebAuthn flow
        const { data: opts } = await api.post('/webauthn/generate-authentication-options', {
          email: null,
        });
        const assertion = await startAuthentication({ optionsJSON: opts.optionsJSON });
        await api.post('/webauthn/verify-authentication', {
          email: null,
          challenge_id: opts.challenge_id,
          credential: assertion,
        });
      }

      // Success — proceed to the change flow
      setConfirmIdentityOpen(false);
      if (confirmIdentityAction === 'email') {
        setNewEmail('');
        setErrorMsg(null);
        setChangeEmailOpen(true);
      }
      if (confirmIdentityAction === 'phone') {
        setPhonePretty('');
        setAgreeSms(false);
        setErrorMsg(null);
        setChangePhoneOpen(true);
      }
    } catch (err: unknown) {
      const e = err as { name?: string; message?: string };
      if (e.name === 'NotAllowedError') {
        setErrorMsg('Biometric verification was cancelled.');
      } else {
        setErrorMsg(e.message || 'Biometric verification failed. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAddPasskey = async () => {
    try {
      const success = await addPasskey();
      // Auto-enable auto-prompt when first passkey is added
      if (success && devices.length === 0) {
        await updateAutoPromptPasskey(true);
      }
    } catch (err) {
      // Error is handled by the hook
      console.error('Failed to add passkey:', err);
    }
  };

  const handleRemovePasskey = async (deviceId: string, deviceName: string) => {
    setDeviceToRemove({ id: deviceId, name: deviceName });
  };

  const confirmRemovePasskey = async () => {
    if (!deviceToRemove) return;

    try {
      await removePasskey(deviceToRemove.id);
      setDeviceToRemove(null);
    } catch (err) {
      // Error is handled by the hook
      console.error('Failed to remove passkey:', err);
    }
  };

  const handleConfirmLogoutAll = async () => {
    try {
      await authApi.signOut('global');
    } finally {
      setConfirmOpen(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      await profileApi.deleteMe();
      clearSession();
      window.location.href = '/login';
    } catch (err) {
      console.error('Failed to delete account:', err);
      showError('Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
    }
  };

  return (
    <>
      {/* Account information */}
      <Panel aria-labelledby="account-information">
        <FormGrid>
          <Block>
            <Title id="account-information">Account information</Title>

            <Stack $gap={4}>
              <RowBetween>
                <SubTitle>Email</SubTitle>
                <ActionText
                  type="button"
                  role="button"
                  tabIndex={0}
                  aria-haspopup="dialog"
                  aria-controls="change-email-dialog"
                  onClick={() => {
                    setConfirmIdentityAction('email');
                    setConfirmIdentityOpen(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setConfirmIdentityAction('email');
                      setConfirmIdentityOpen(true);
                    }
                  }}
                >
                  Change
                </ActionText>
              </RowBetween>

              <MutedText style={{ color: '#E9ECF5' }}>{userEmail || '—'}</MutedText>
            </Stack>

            <Stack $gap={4}>
              <RowBetween>
                <SubTitle>Phone</SubTitle>
                <ActionText
                  type="button"
                  onClick={() => {
                    setConfirmIdentityAction('phone');
                    setConfirmIdentityOpen(true);
                  }}
                  aria-label="Open change phone panel"
                  aria-haspopup="dialog"
                  aria-controls="change-phone-dialog"
                >
                  Change
                </ActionText>
              </RowBetween>
              <MutedText style={{ color: '#E9ECF5' }}>{profile?.mobile_number || '—'}</MutedText>
            </Stack>
          </Block>
        </FormGrid>
      </Panel>

      {/* Sessions & security */}
      <Panel aria-labelledby="sessions-and-security">
        <FormGrid>
          <Block>
            <Title id="sessions-and-security">Sessions &amp; security</Title>

            <Stack $gap={4}>
              <RowBetween>
                <SubTitle>Log out of all devices</SubTitle>
                <ActionText
                  type="button"
                  role="button"
                  tabIndex={0}
                  aria-haspopup="dialog"
                  aria-controls="logout-everywhere-dialog"
                  onClick={() => setConfirmOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setConfirmOpen(true);
                    }
                  }}
                >
                  Log out
                </ActionText>
              </RowBetween>

              <MutedText style={{ color: '#E9ECF5' }}>
                You&apos;ll need to sign in again
                <br />
                on all devices.
              </MutedText>
            </Stack>

            <RowBetween>
              <SubTitle>Change password</SubTitle>
              <ActionText
                type="button"
                onClick={onChangePasswordRequested}
                aria-label="Open change password panel"
              >
                Update
              </ActionText>
            </RowBetween>
          </Block>
        </FormGrid>
      </Panel>

      {/* Biometrics */}
      <Panel aria-labelledby="passkeys-title">
        <Block>
          <Stack $gap={8}>
            <Title id="passkeys-title">Biometrics</Title>
            <PasskeyText>
              Use your device&apos;s biometrics (Face ID, fingerprint, Windows Hello) for
              password-free sign-in.
            </PasskeyText>
          </Stack>

          {error && (
            <HelperText role="alert" style={{ color: '#ff6b6b' }}>
              {error}
            </HelperText>
          )}

          {devices.length === 0 && !loading && (
            <MutedText>No biometric credentials registered yet.</MutedText>
          )}

          {devices.map((device) => {
            const deviceName = device.friendly_name || 'Biometric credential';
            const addedDate = new Date(device.created_at).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric',
            });
            const lastUsed = device.last_used_at
              ? new Date(device.last_used_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })
              : null;

            return (
              <DeviceRow
                key={device.id}
                name={deviceName}
                authType={device.auth_type}
                added={addedDate}
                lastUsed={lastUsed}
                onRemove={() => handleRemovePasskey(device.id, deviceName)}
              />
            );
          })}
        </Block>

        <VSpace $h={24} />

        {biometricAvailable === false && (
          <>
            <MutedText>
              Biometrics not available on this device. Ensure your device has fingerprint, Face ID,
              or Windows Hello configured.
            </MutedText>
            <VSpace $h={16} />
          </>
        )}

        <OnboardBtn
          type="button"
          style={{ width: '100%' }}
          onClick={handleAddPasskey}
          disabled={loading || devices.length >= 10 || biometricAvailable === false}
        >
          <span>
            {loading
              ? 'Enrolling biometrics...'
              : devices.length >= 10
                ? 'Maximum credentials reached'
                : error
                  ? 'Try again'
                  : 'Enroll biometrics'}
          </span>
        </OnboardBtn>

        <VSpace $h={16} />
      </Panel>

      {/* This device */}
      <Panel aria-labelledby="this-device-title">
        <Block>
          <Stack $gap={8}>
            <Title id="this-device-title">This device</Title>
            <MutedSubTitle>
              Use your device's Face ID, Touch ID, or
              <br /> security key for password-free sign-in.
            </MutedSubTitle>
          </Stack>

          <ThisDeviceAutoPrompt
            autoPromptEnabled={settings.autoPromptPasskey}
            onToggle={updateAutoPromptPasskey}
            hasPasskeys={devices.length > 0}
            onAddPasskey={handleAddPasskey}
          />
        </Block>
      </Panel>

      {/* Delete account */}
      <Panel aria-labelledby="delete-account-title">
        <Block>
          <Stack $gap={8}>
            <Title id="delete-account-title">Delete your account</Title>
            <MutedText>This action cannot be undone.</MutedText>
            <VSpace $h={12} />
            <DeleteAccountButton
              type="button"
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete account'}
            </DeleteAccountButton>
            <VSpace $h={16} />
          </Stack>
        </Block>
      </Panel>

      {/* Confirm identity dialog (shared by email & phone change) */}
      <AlertDialog.Root open={confirmIdentityOpen} onOpenChange={setConfirmIdentityOpen}>
        <AlertDialog.Portal container={document.body}>
          <AlertOverlay onClick={() => setConfirmIdentityOpen(false)} />

          <AlertDialog.Content
            asChild
            id="confirm-identity-dialog"
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <AlertContentShell
              style={{
                maxWidth: '100%',
                width: 'calc(100% - 32px)',
                // height: '425px',
                display: 'flex',
                flexDirection: 'column',
                // justifyContent: 'space-between',
                gap: 32,
                alignItems: 'center',
              }}
            >
              <div>
                <AlertDialog.Title asChild>
                  <Title style={{ textAlign: 'center' }} as="h2">
                    Confirm your identity
                  </Title>
                </AlertDialog.Title>
                <VSpace $h={8} />
                <AlertDialog.Description asChild>
                  <MutedText style={{ textAlign: 'center' }}>
                    For security, please confirm your identity before updating your{' '}
                    {confirmIdentityAction === 'email' ? 'email' : 'phone'}.
                  </MutedText>
                </AlertDialog.Description>
              </div>

              {errorMsg && (
                <HelperText role="alert" style={{ color: '#D65F46' }}>
                  {errorMsg}
                </HelperText>
              )}

              <Stack style={{ width: '100%' }} $gap={12}>
                {devices.length > 0 && (
                  <OnboardBtn
                    type="button"
                    style={{ width: '100%' }}
                    disabled={saving}
                    onClick={handleBiometricVerify}
                  >
                    {saving ? 'Verifying...' : 'Use Touch/Face ID'}
                  </OnboardBtn>
                )}
                <CancelTextButton
                  type="button"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                  onClick={() => {
                    setConfirmIdentityOpen(false);
                    setPassword('');
                    setShowPassword(false);
                    setErrorMsg(null);
                    setPasswordModalOpen(true);
                  }}
                >
                  Enter password
                </CancelTextButton>
              </Stack>
            </AlertContentShell>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Enter password dialog */}
      <AlertDialog.Root open={passwordModalOpen} onOpenChange={setPasswordModalOpen}>
        <AlertDialog.Portal container={document.body}>
          <AlertOverlay onClick={() => setPasswordModalOpen(false)} />

          <AlertDialog.Content
            asChild
            id="enter-password-dialog"
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <AlertContentShell
              style={{
                maxWidth: '100%',
                width: 'calc(100% - 32px)',
                // height: '425px',
                display: 'flex',
                flexDirection: 'column',
                // justifyContent: 'space-between',
                alignItems: 'center',
                gap: 32,
              }}
            >
              <div style={{ width: '100%' }}>
                <AlertDialog.Title asChild>
                  <Title style={{ textAlign: 'center' }} as="h2">
                    Enter your password
                  </Title>
                </AlertDialog.Title>
                <VSpace $h={8} />
                <AlertDialog.Description asChild>
                  <MutedText style={{ textAlign: 'center' }}>
                    For security, enter your password to continue.
                  </MutedText>
                </AlertDialog.Description>
              </div>

              <Stack style={{ width: '100%' }} $gap={16}>
                <div
                  style={{
                    opacity: forgotSending ? 0.5 : 1,
                    pointerEvents: forgotSending ? 'none' : 'auto',
                    transition: 'opacity 0.2s',
                  }}
                >
                  <InputWrap>
                    <InputWithToggle
                      $hasToggle
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errorMsg) setErrorMsg(null);
                      }}
                      style={errorMsg ? { borderColor: '#D65F46' } : undefined}
                      autoFocus
                      disabled={forgotSending || saving}
                    />
                    <EyeToggle
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      disabled={forgotSending || saving}
                    >
                      {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </EyeToggle>
                  </InputWrap>
                  {errorMsg && (
                    <HelperText role="alert" style={{ color: '#D65F46', paddingTop: 4 }}>
                      {errorMsg}
                    </HelperText>
                  )}
                </div>
                <OnboardBtn
                  type="button"
                  style={{
                    width: '100%',
                    opacity: forgotSending ? 0.5 : 1,
                    transition: 'opacity 0.2s',
                  }}
                  disabled={saving || forgotSending}
                  onClick={async () => {
                    if (!password.trim()) {
                      setErrorMsg('Please enter your password.');
                      return;
                    }
                    setErrorMsg(null);
                    setSaving(true);
                    try {
                      await authApi.signinPassword(userEmail, password);
                      setPasswordModalOpen(false);
                      if (confirmIdentityAction === 'email') {
                        setNewEmail('');
                        setErrorMsg(null);
                        setChangeEmailOpen(true);
                      }
                      if (confirmIdentityAction === 'phone') {
                        setPhonePretty('');
                        setAgreeSms(false);
                        setErrorMsg(null);
                        setChangePhoneOpen(true);
                      }
                    } catch (err: unknown) {
                      const e = err as {
                        response?: { status?: number; data?: { detail?: string } };
                      };
                      const msg =
                        e?.response?.status === 401 || e?.response?.status === 400
                          ? 'Incorrect password.'
                          : e?.response?.data?.detail || 'Verification failed. Please try again.';
                      setErrorMsg(msg);
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  {saving ? 'Verifying...' : 'Continue'}
                </OnboardBtn>
                <CancelTextButton
                  type="button"
                  disabled={saving || forgotSending}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.15)',
                    opacity: forgotSending ? 0.5 : 1,
                    transition: 'opacity 0.2s',
                  }}
                  onClick={() => {
                    setPasswordModalOpen(false);
                    setConfirmIdentityOpen(true);
                  }}
                >
                  Back
                </CancelTextButton>
                <ActionText
                  type="button"
                  style={{ textAlign: 'center', width: '100%', height: '44px' }}
                  onClick={async () => {
                    setErrorMsg(null);
                    setForgotSending(true);
                    try {
                      await authApi.forgotPassword(userEmail);
                      setPasswordModalOpen(false);
                      setForgotCode('');
                      setForgotCodeStatus('idle');
                      setErrorMsg(null);
                      startResendCooldown();
                      setForgotVerifyOpen(true);
                    } catch (err: unknown) {
                      const e = err as { response?: { data?: { detail?: string } } };
                      setErrorMsg(e?.response?.data?.detail || 'Failed to send reset code.');
                    } finally {
                      setForgotSending(false);
                    }
                  }}
                  disabled={forgotSending || saving}
                >
                  {forgotSending ? 'Sending code...' : 'Forgot your password?'}
                </ActionText>
              </Stack>
            </AlertContentShell>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Forgot password - verify code dialog */}
      <AlertDialog.Root open={forgotVerifyOpen} onOpenChange={setForgotVerifyOpen}>
        <AlertDialog.Portal container={document.body}>
          <AlertOverlay onClick={() => setForgotVerifyOpen(false)} />

          <AlertDialog.Content
            asChild
            id="forgot-verify-dialog"
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <AlertContentShell
              style={{
                maxWidth: '100%',
                width: 'calc(100% - 32px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 32,
              }}
            >
              <div style={{ width: '100%' }}>
                <AlertDialog.Title asChild>
                  <Title style={{ textAlign: 'center' }} as="h2">
                    Verify your email
                  </Title>
                </AlertDialog.Title>
                <VSpace $h={8} />
                <AlertDialog.Description asChild>
                  <MutedText style={{ textAlign: 'center' }}>
                    Enter the code sent to <br />
                    <span style={{ color: '#E9ECF5' }}>{userEmail}</span>
                  </MutedText>
                </AlertDialog.Description>
              </div>

              <div style={{ width: '100%' }}>
                <Label style={{ marginBottom: 8, display: 'block', fontFamily: 'Inter' }}>
                  Verification code
                </Label>
                <VerificationCodeInput
                  length={6}
                  value={forgotCode}
                  onChange={(val) => {
                    setForgotCode(val);
                    if (forgotCodeStatus === 'error') {
                      setForgotCodeStatus('idle');
                      setErrorMsg(null);
                    }
                  }}
                  status={forgotCodeStatus}
                />
                {errorMsg && (
                  <HelperText role="alert" style={{ color: '#D65F46', marginTop: 8 }}>
                    {errorMsg}
                  </HelperText>
                )}
              </div>

              <Stack style={{ width: '100%' }} $gap={16}>
                <OnboardBtn
                  type="button"
                  style={{ width: '100%' }}
                  disabled={saving}
                  onClick={async () => {
                    if (forgotCode.length < 6) {
                      setErrorMsg('Please enter the full 6-digit code.');
                      setForgotCodeStatus('error');
                      return;
                    }
                    setErrorMsg(null);
                    setSaving(true);
                    try {
                      const res = await authApi.verifyResetCode(userEmail, forgotCode);
                      if (res?.verified === false) {
                        setForgotCodeStatus('error');
                        setErrorMsg('Incorrect code – please try again.');
                        return;
                      }
                      setForgotCodeStatus('success');
                      setTimeout(() => {
                        setForgotVerifyOpen(false);
                        setNewPassword('');
                        setConfirmPassword('');
                        setErrorMsg(null);
                        setResetPasswordOpen(true);
                      }, 600);
                    } catch (err: unknown) {
                      const e = err as { response?: { data?: { detail?: string } } };
                      setForgotCodeStatus('error');
                      setErrorMsg(
                        e?.response?.data?.detail || 'Incorrect code – please try again.'
                      );
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  {saving ? 'Verifying...' : 'Verify email'}
                </OnboardBtn>
                <MutedText style={{ textAlign: 'center' }}>
                  Didn&apos;t receive a code?{' '}
                  {resendSeconds > 0 ? (
                    <span style={{ color: '#9AA7C7' }}>Resend in {resendSeconds}s</span>
                  ) : (
                    <ActionText
                      type="button"
                      as="span"
                      style={{
                        display: 'inline',
                        cursor: saving ? 'default' : 'pointer',
                        opacity: saving ? 0.5 : 1,
                      }}
                      onClick={
                        saving
                          ? undefined
                          : async () => {
                              try {
                                await authApi.forgotPassword(userEmail);
                                setForgotCode('');
                                setForgotCodeStatus('idle');
                                setErrorMsg(null);
                                startResendCooldown();
                                showSuccess('Code resent.');
                              } catch (err: unknown) {
                                const e = err as { response?: { data?: { detail?: string } } };
                                setErrorMsg(e?.response?.data?.detail || 'Failed to resend code.');
                              }
                            }
                      }
                    >
                      Resend
                    </ActionText>
                  )}
                </MutedText>
                <CancelTextButton
                  type="button"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                  onClick={() => {
                    setForgotVerifyOpen(false);
                    setErrorMsg(null);
                    setPasswordModalOpen(true);
                  }}
                >
                  Back
                </CancelTextButton>
              </Stack>
            </AlertContentShell>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Reset password dialog */}
      <AlertDialog.Root open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <AlertDialog.Portal container={document.body}>
          <AlertOverlay onClick={() => setResetPasswordOpen(false)} />

          <AlertDialog.Content
            asChild
            id="reset-password-dialog"
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <AlertContentShell
              style={{
                maxWidth: '100%',
                width: 'calc(100% - 32px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 32,
              }}
            >
              <div style={{ width: '100%' }}>
                <AlertDialog.Title asChild>
                  <Title style={{ textAlign: 'center' }} as="h2">
                    Reset password
                  </Title>
                </AlertDialog.Title>
                <VSpace $h={8} />
                <AlertDialog.Description asChild>
                  <MutedText style={{ textAlign: 'center' }}>Enter your new password.</MutedText>
                </AlertDialog.Description>
              </div>

              <div style={{ width: '100%' }}>
                <FloatingInput
                  id="reset-new-password"
                  label="Create a password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setErrorMsg(null);
                  }}
                  showToggle
                  disabled={saving}
                  autoFocus
                  {...(errorMsg && errorMsg.includes('weak') ? { errorText: ' ' } : {})}
                />

                <PasswordStrengthBar
                  score={calcScore(newPassword)}
                  allRequirementsMet={
                    newPassword.length >= 8 &&
                    /[A-Z]/.test(newPassword) &&
                    /[a-z]/.test(newPassword) &&
                    /\d/.test(newPassword) &&
                    /[^A-Za-z0-9]/.test(newPassword)
                  }
                />

                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: '8px 0 0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {(
                    [
                      [newPassword.length >= 8, '8\u00A0+ characters'],
                      [/[A-Z]/.test(newPassword), 'Upper-case letter'],
                      [/[a-z]/.test(newPassword), 'Lower-case letter'],
                      [/\d/.test(newPassword), 'Number'],
                      [/[^A-Za-z0-9]/.test(newPassword), 'Symbol\u00A0(!@#$\u2026)'],
                    ] as [boolean, string][]
                  ).map(([ok, label]) => (
                    <li
                      key={label}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        paddingTop: 4,
                        paddingLeft: 17,
                      }}
                    >
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          color: '#8097EA',
                          lineHeight: 0,
                        }}
                      >
                        {ok ? <FiCheckCircle size={14} /> : <FiCircle size={14} />}
                      </span>
                      <span
                        style={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: 12,
                          fontWeight: ok ? 600 : 400,
                          color: ok ? '#8097EA' : '#B7BDCBCC',
                        }}
                      >
                        {label}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div style={{ width: '100%' }}>
                <FloatingInput
                  id="reset-confirm-password"
                  label="Confirm password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setErrorMsg(null);
                  }}
                  showToggle
                  disabled={saving}
                  {...(errorMsg && errorMsg.includes('match') ? { errorText: ' ' } : {})}
                />
              </div>

              {errorMsg && (
                <HelperText role="alert" style={{ color: '#D65F46', width: '100%' }}>
                  {errorMsg}
                </HelperText>
              )}

              <Stack style={{ width: '100%' }} $gap={16}>
                <OnboardBtn
                  type="button"
                  style={{ width: '100%' }}
                  disabled={saving}
                  onClick={async () => {
                    const reqs = {
                      length: newPassword.length >= 8,
                      upper: /[A-Z]/.test(newPassword),
                      lower: /[a-z]/.test(newPassword),
                      number: /\d/.test(newPassword),
                      symbol: /[^A-Za-z0-9]/.test(newPassword),
                    };
                    const checklistOk = Object.values(reqs).every(Boolean);
                    const isTooWeak = !newPassword || !checklistOk || calcScore(newPassword) < 10;

                    if (isTooWeak) {
                      setErrorMsg(
                        'Password too weak. Add upper/lower case letters, numbers and symbols.'
                      );
                      return;
                    }
                    if (!confirmPassword || newPassword !== confirmPassword) {
                      setErrorMsg('Passwords do not match.');
                      return;
                    }
                    setErrorMsg(null);
                    setSaving(true);
                    try {
                      await authApi.resetPasswordWithCode(userEmail, forgotCode, newPassword);
                      try {
                        await signIn(userEmail, newPassword);
                        await syncSession();
                      } catch {
                        // Silent — user may need to re-login later
                      }
                      setResetPasswordOpen(false);
                      setPasswordResetSuccess(true);
                    } catch (err: unknown) {
                      const e = err as { response?: { data?: { detail?: string } } };
                      setErrorMsg(
                        e?.response?.data?.detail || 'Failed to reset password. Please try again.'
                      );
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  {saving ? 'Resetting...' : 'Reset password'}
                </OnboardBtn>
                <CancelTextButton
                  type="button"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                  onClick={() => {
                    setResetPasswordOpen(false);
                    setErrorMsg(null);
                  }}
                >
                  Cancel
                </CancelTextButton>
              </Stack>
            </AlertContentShell>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Password reset success dialog */}
      <AlertDialog.Root open={passwordResetSuccess} onOpenChange={setPasswordResetSuccess}>
        <AlertDialog.Portal container={document.body}>
          <AlertOverlay onClick={() => setPasswordResetSuccess(false)} />

          <AlertDialog.Content
            asChild
            id="password-reset-success-dialog"
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <AlertContentShell
              style={{
                maxWidth: '100%',
                width: 'calc(100% - 32px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 32,
              }}
            >
              <div style={{ width: '100%' }}>
                <AlertDialog.Title asChild>
                  <Title style={{ textAlign: 'center', color: '#5E84ED' }} as="h2">
                    Password reset
                  </Title>
                </AlertDialog.Title>
                <VSpace $h={8} />
                <AlertDialog.Description asChild>
                  <MutedText style={{ textAlign: 'center' }}>
                    Your password has been reset successfully.
                  </MutedText>
                </AlertDialog.Description>
              </div>

              <OnboardBtn
                type="button"
                style={{ width: '100%' }}
                onClick={() => setPasswordResetSuccess(false)}
              >
                Done
              </OnboardBtn>
            </AlertContentShell>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Change email dialog */}
      <AlertDialog.Root open={changeEmailOpen} onOpenChange={setChangeEmailOpen}>
        <AlertDialog.Portal container={document.body}>
          <AlertOverlay onClick={() => setChangeEmailOpen(false)} />

          <AlertDialog.Content
            asChild
            id="change-email-dialog"
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <AlertContentShell
              style={{
                maxWidth: '100%',
                width: 'calc(100% - 32px)',
                // height: '425px',
                display: 'flex',
                flexDirection: 'column',
                // justifyContent: 'space-between',
                gap: 32,
                alignItems: 'center',
              }}
            >
              <div style={{ width: '100%' }}>
                <AlertDialog.Title asChild>
                  <Title style={{ textAlign: 'center' }} as="h2">
                    Change email
                  </Title>
                </AlertDialog.Title>
                <VSpace $h={8} />
                <AlertDialog.Description asChild>
                  <MutedText style={{ textAlign: 'center' }}>
                    Enter the new email address for your account.
                  </MutedText>
                </AlertDialog.Description>
              </div>

              <Stack style={{ width: '100%' }} $gap={16}>
                <div>
                  <Input
                    type="email"
                    placeholder="Enter a new email"
                    value={newEmail}
                    onChange={(e) => {
                      setNewEmail(e.target.value);
                      setErrorMsg(null);
                    }}
                    style={errorMsg ? { borderColor: '#D65F46' } : undefined}
                    autoFocus
                    disabled={saving}
                  />
                  {errorMsg && (
                    <HelperText role="alert" style={{ color: '#D65F46', paddingTop: 4 }}>
                      {errorMsg}
                    </HelperText>
                  )}
                </div>
                <OnboardBtn
                  type="button"
                  style={{ width: '100%' }}
                  disabled={saving}
                  onClick={async () => {
                    if (!newEmail.trim()) {
                      setErrorMsg('Please enter an email address.');
                      return;
                    }
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim())) {
                      setErrorMsg('Please enter a valid email address.');
                      return;
                    }
                    setErrorMsg(null);
                    setSaving(true);
                    try {
                      await authApi.requestEmailChangeOtp(newEmail.trim());
                      setChangeEmailOpen(false);
                      setVerifyCode('');
                      setVerifyCodeStatus('idle');
                      startResendCooldown();
                      setVerifyEmailOpen(true);
                    } catch (err: unknown) {
                      const e = err as { response?: { data?: { detail?: string } } };
                      setErrorMsg(
                        e?.response?.data?.detail || 'Failed to send code. Please try again.'
                      );
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  {saving ? 'Sending...' : 'Send code'}
                </OnboardBtn>
                <CancelTextButton
                  type="button"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                  onClick={() => {
                    setChangeEmailOpen(false);
                    setErrorMsg(null);
                  }}
                >
                  Cancel
                </CancelTextButton>
              </Stack>
            </AlertContentShell>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Verify email dialog */}
      <AlertDialog.Root open={verifyEmailOpen} onOpenChange={setVerifyEmailOpen}>
        <AlertDialog.Portal container={document.body}>
          <AlertOverlay onClick={() => setVerifyEmailOpen(false)} />

          <AlertDialog.Content
            asChild
            id="verify-email-dialog"
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <AlertContentShell
              style={{
                maxWidth: '100%',
                width: 'calc(100% - 32px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 32,
              }}
            >
              <div style={{ width: '100%' }}>
                <AlertDialog.Title asChild>
                  <Title style={{ textAlign: 'center' }} as="h2">
                    Verify your email
                  </Title>
                </AlertDialog.Title>
                <VSpace $h={8} />
                <AlertDialog.Description asChild>
                  <MutedText style={{ textAlign: 'center' }}>
                    Enter the code sent to <br />
                    <span style={{ color: '#E9ECF5' }}>{newEmail}</span>
                  </MutedText>
                </AlertDialog.Description>
              </div>

              <div style={{ width: '100%' }}>
                <Label style={{ marginBottom: 8, display: 'block', fontFamily: 'Inter' }}>
                  Verification code
                </Label>
                <VerificationCodeInput
                  length={6}
                  value={verifyCode}
                  onChange={(val) => {
                    setVerifyCode(val);
                    if (verifyCodeStatus === 'error') {
                      setVerifyCodeStatus('idle');
                      setErrorMsg(null);
                    }
                  }}
                  status={verifyCodeStatus}
                />
                {errorMsg && (
                  <HelperText role="alert" style={{ color: '#D65F46', marginTop: 8 }}>
                    {errorMsg}
                  </HelperText>
                )}
              </div>

              <Stack style={{ width: '100%' }} $gap={16}>
                <OnboardBtn
                  type="button"
                  style={{ width: '100%' }}
                  disabled={saving}
                  onClick={async () => {
                    if (verifyCode.length < 6) {
                      setErrorMsg('Please enter the full 6-digit code.');
                      setVerifyCodeStatus('error');
                      return;
                    }
                    setErrorMsg(null);
                    setSaving(true);
                    try {
                      await authApi.verifyEmailChangeOtp(newEmail.trim(), verifyCode);
                      await syncSession();
                      setVerifyEmailOpen(false);
                      setEmailUpdatedOpen(true);
                    } catch (err: unknown) {
                      const e = err as { response?: { data?: { detail?: string } } };
                      setVerifyCodeStatus('error');
                      setErrorMsg(
                        e?.response?.data?.detail || 'Incorrect code – please try again.'
                      );
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  {saving ? 'Verifying...' : 'Verify email'}
                </OnboardBtn>
                <MutedText style={{ textAlign: 'center' }}>
                  Didn&apos;t receive a code?{' '}
                  {resendSeconds > 0 ? (
                    <span style={{ color: '#9AA7C7' }}>Resend in {resendSeconds}s</span>
                  ) : (
                    <ActionText
                      type="button"
                      as="span"
                      style={{
                        display: 'inline',
                        cursor: saving ? 'default' : 'pointer',
                        opacity: saving ? 0.5 : 1,
                      }}
                      onClick={
                        saving
                          ? undefined
                          : async () => {
                              try {
                                await authApi.requestEmailChangeOtp(newEmail.trim());
                                setVerifyCode('');
                                setVerifyCodeStatus('idle');
                                setErrorMsg(null);
                                startResendCooldown();
                                showSuccess('Code resent.');
                              } catch (err: unknown) {
                                const e = err as { response?: { data?: { detail?: string } } };
                                setErrorMsg(e?.response?.data?.detail || 'Failed to resend code.');
                              }
                            }
                      }
                    >
                      Resend
                    </ActionText>
                  )}
                </MutedText>
                <CancelTextButton
                  type="button"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                  onClick={() => {
                    setVerifyEmailOpen(false);
                    setChangeEmailOpen(true);
                  }}
                >
                  Back
                </CancelTextButton>
              </Stack>
            </AlertContentShell>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Email updated success dialog */}
      <AlertDialog.Root open={emailUpdatedOpen} onOpenChange={setEmailUpdatedOpen}>
        <AlertDialog.Portal container={document.body}>
          <AlertOverlay onClick={() => setEmailUpdatedOpen(false)} />

          <AlertDialog.Content
            asChild
            id="email-updated-dialog"
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <AlertContentShell
              style={{
                maxWidth: '100%',
                width: 'calc(100% - 32px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 32,
              }}
            >
              <div style={{ width: '100%' }}>
                <AlertDialog.Title asChild>
                  <Title style={{ textAlign: 'center', color: '#5E84ED' }} as="h2">
                    Email updated
                  </Title>
                </AlertDialog.Title>
                <VSpace $h={8} />
                <AlertDialog.Description asChild>
                  <MutedText style={{ textAlign: 'center' }}>
                    Your account email has been updated successfully.
                  </MutedText>
                </AlertDialog.Description>
              </div>

              <OnboardBtn
                type="button"
                style={{ width: '100%' }}
                onClick={() => setEmailUpdatedOpen(false)}
              >
                Done
              </OnboardBtn>
            </AlertContentShell>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Change phone number dialog */}
      <AlertDialog.Root open={changePhoneOpen} onOpenChange={setChangePhoneOpen}>
        <AlertDialog.Portal container={document.body}>
          <AlertOverlay onClick={() => setChangePhoneOpen(false)} />

          <AlertDialog.Content
            asChild
            id="change-phone-dialog"
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <AlertContentShell
              style={{
                maxWidth: '100%',
                width: 'calc(100% - 32px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 32,
              }}
            >
              <div style={{ width: '100%' }}>
                <AlertDialog.Title asChild>
                  <Title style={{ textAlign: 'center' }} as="h2">
                    Change phone number
                  </Title>
                </AlertDialog.Title>
                <VSpace $h={8} />
                <AlertDialog.Description asChild>
                  <MutedText style={{ textAlign: 'center' }}>
                    Enter the phone number you want to use for your account.
                  </MutedText>
                </AlertDialog.Description>
              </div>

              <div style={{ width: '100%' }}>
                <DropdownContainer ref={dropdownRef}>
                  <FloatingInput
                    id="change-phone"
                    label="Phone number"
                    value={phonePretty}
                    onChange={handlePhoneInput}
                    inputMode="tel"
                    autoComplete="tel"
                    disabled={saving}
                    {...(errorMsg && (errorMsg.includes('phone') || errorMsg.includes('number'))
                      ? { errorText: ' ' }
                      : {})}
                    prefix={
                      <Prefix
                        onClick={saving ? undefined : toggleDropdown}
                        style={saving ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
                      >
                        <img
                          src="/phone-prefix-dropdown.svg"
                          alt="Phone prefix dropdown"
                          style={{ pointerEvents: 'none', userSelect: 'none' }}
                        />
                        <Flag src={selectedCountry.flag} alt={`${selectedCountry.name} flag`} />
                        <PrefixText>{selectedCountry.dialCode}</PrefixText>
                      </Prefix>
                    }
                    prefixWidth={selectedCountry.code === 'UK' ? 105 : 90}
                  />
                  <DropdownMenu isOpen={isDropdownOpen} isClosing={isDropdownClosing}>
                    {COUNTRIES.map((country) => (
                      <DropdownOption
                        key={country.code}
                        isSelected={selectedCountry.code === country.code}
                        onClick={() => selectCountry(country)}
                      >
                        <OptionLeft>
                          <OptionFlag src={country.flag} alt={`${country.name} flag`} />
                          <OptionName>{country.name}</OptionName>
                        </OptionLeft>
                        <OptionCode>{country.dialCode}</OptionCode>
                      </DropdownOption>
                    ))}
                  </DropdownMenu>
                </DropdownContainer>
                {errorMsg && (
                  <HelperText role="alert" style={{ color: '#D65F46', marginTop: 4 }}>
                    {errorMsg}
                  </HelperText>
                )}
              </div>

              <CheckboxRow>
                <CheckboxInput
                  type="checkbox"
                  id="sms-consent-change"
                  checked={agreeSms}
                  disabled={saving}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setAgreeSms(e.target.checked);
                    if (e.target.checked) setErrorMsg(null);
                  }}
                />
                <CheckboxLabel htmlFor="sms-consent-change">
                  I agree to receive SMS updates from Mailander. Frequency varies. Msg &amp; Data
                  rates may apply. Reply STOP to opt out. See our{' '}
                  <a
                    style={{ color: '#5E84ED' }}
                    href="https://mailander.com/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms
                  </a>{' '}
                  and{' '}
                  <a
                    style={{ color: '#5E84ED' }}
                    href="https://mailander.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy policy
                  </a>
                  .
                </CheckboxLabel>
              </CheckboxRow>

              <Stack style={{ width: '100%' }} $gap={16}>
                <OnboardBtn
                  type="button"
                  style={{ width: '100%' }}
                  disabled={saving}
                  onClick={async () => {
                    if (!isPhoneValid) {
                      setErrorMsg('Please enter a valid phone number.');
                      return;
                    }
                    if (!agreeSms) {
                      setErrorMsg('Please agree to receive SMS updates to continue.');
                      return;
                    }
                    setErrorMsg(null);
                    setSaving(true);
                    const res = await sendCode(fullPhoneNumber);
                    setSaving(false);
                    if (!res.ok) {
                      setErrorMsg(res.message);
                      return;
                    }
                    setChangePhoneOpen(false);
                    setPhoneCode('');
                    setPhoneCodeStatus('idle');
                    startResendCooldown();
                    setVerifyPhoneOpen(true);
                  }}
                >
                  {saving ? 'Sending...' : 'Send code'}
                </OnboardBtn>
                <CancelTextButton
                  type="button"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                  onClick={() => {
                    setChangePhoneOpen(false);
                    setErrorMsg(null);
                  }}
                >
                  Back
                </CancelTextButton>
              </Stack>
            </AlertContentShell>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Verify phone dialog */}
      <AlertDialog.Root open={verifyPhoneOpen} onOpenChange={setVerifyPhoneOpen}>
        <AlertDialog.Portal container={document.body}>
          <AlertOverlay onClick={() => setVerifyPhoneOpen(false)} />

          <AlertDialog.Content
            asChild
            id="verify-phone-dialog"
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <AlertContentShell
              style={{
                maxWidth: '100%',
                width: 'calc(100% - 32px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 32,
              }}
            >
              <div style={{ width: '100%' }}>
                <AlertDialog.Title asChild>
                  <Title style={{ textAlign: 'center' }} as="h2">
                    Verify your phone
                  </Title>
                </AlertDialog.Title>
                <VSpace $h={8} />
                <AlertDialog.Description asChild>
                  <MutedText style={{ textAlign: 'center' }}>
                    Enter the code sent to <br />
                    <span style={{ color: '#E9ECF5' }}>
                      {selectedCountry.dialCode} {phonePretty}
                    </span>
                  </MutedText>
                </AlertDialog.Description>
              </div>

              <div style={{ width: '100%' }}>
                <Label style={{ marginBottom: 8, display: 'block', fontFamily: 'Inter' }}>
                  Verification code
                </Label>
                <VerificationCodeInput
                  length={6}
                  value={phoneCode}
                  onChange={(val) => {
                    setPhoneCode(val);
                    if (phoneCodeStatus === 'error') {
                      setPhoneCodeStatus('idle');
                      setErrorMsg(null);
                    }
                  }}
                  status={phoneCodeStatus}
                />
                {errorMsg && (
                  <HelperText role="alert" style={{ color: '#D65F46', marginTop: 8 }}>
                    {errorMsg}
                  </HelperText>
                )}
              </div>

              <Stack style={{ width: '100%' }} $gap={16}>
                <OnboardBtn
                  type="button"
                  style={{ width: '100%' }}
                  disabled={saving}
                  onClick={async () => {
                    if (phoneCode.length < 6) {
                      setErrorMsg('Please enter the full 6-digit code.');
                      setPhoneCodeStatus('error');
                      return;
                    }
                    setErrorMsg(null);
                    setSaving(true);
                    const res = await checkCode(fullPhoneNumber, phoneCode);
                    if (!res.ok || !res.verified) {
                      setPhoneCodeStatus('error');
                      setErrorMsg(res.message || 'Incorrect code – please try again.');
                      setSaving(false);
                      return;
                    }
                    try {
                      await profileApi.updateMe({ mobile_number: fullPhoneNumber });
                      await syncSession();
                      setVerifyPhoneOpen(false);
                      setPhoneUpdatedOpen(true);
                    } catch (err: unknown) {
                      const e = err as { response?: { data?: { detail?: string } } };
                      setErrorMsg(e?.response?.data?.detail || 'Failed to update phone number.');
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  {saving ? 'Verifying...' : 'Verify phone'}
                </OnboardBtn>
                <MutedText style={{ textAlign: 'center' }}>
                  Didn&apos;t receive a code?{' '}
                  {resendSeconds > 0 ? (
                    <span style={{ color: '#9AA7C7' }}>Resend in {resendSeconds}s</span>
                  ) : (
                    <ActionText
                      type="button"
                      as="span"
                      style={{
                        display: 'inline',
                        cursor: saving ? 'default' : 'pointer',
                        opacity: saving ? 0.5 : 1,
                      }}
                      onClick={
                        saving
                          ? undefined
                          : async () => {
                              const res = await sendCode(fullPhoneNumber);
                              if (!res.ok) {
                                setErrorMsg(res.message);
                                return;
                              }
                              setPhoneCode('');
                              setPhoneCodeStatus('idle');
                              setErrorMsg(null);
                              startResendCooldown();
                              showSuccess('Code resent.');
                            }
                      }
                    >
                      Resend
                    </ActionText>
                  )}
                </MutedText>
                <CancelTextButton
                  type="button"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                  onClick={() => {
                    setVerifyPhoneOpen(false);
                    setErrorMsg(null);
                    setChangePhoneOpen(true);
                  }}
                >
                  Back
                </CancelTextButton>
              </Stack>
            </AlertContentShell>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Phone updated success dialog */}
      <AlertDialog.Root open={phoneUpdatedOpen} onOpenChange={setPhoneUpdatedOpen}>
        <AlertDialog.Portal container={document.body}>
          <AlertOverlay onClick={() => setPhoneUpdatedOpen(false)} />

          <AlertDialog.Content
            asChild
            id="phone-updated-dialog"
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <AlertContentShell
              style={{
                maxWidth: '100%',
                width: 'calc(100% - 32px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 32,
              }}
            >
              <div style={{ width: '100%' }}>
                <AlertDialog.Title asChild>
                  <Title style={{ textAlign: 'center', color: '#5E84ED' }} as="h2">
                    Phone number updated
                  </Title>
                </AlertDialog.Title>
                <VSpace $h={8} />
                <AlertDialog.Description asChild>
                  <MutedText style={{ textAlign: 'center' }}>
                    Your account phone number has been updated successfully.
                  </MutedText>
                </AlertDialog.Description>
              </div>

              <OnboardBtn
                type="button"
                style={{ width: '100%' }}
                onClick={() => setPhoneUpdatedOpen(false)}
              >
                Done
              </OnboardBtn>
            </AlertContentShell>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Log out everywhere dialog */}
      <AlertDialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialog.Portal container={document.body}>
          <AlertOverlay onClick={() => setConfirmOpen(false)} />

          <AlertDialog.Content
            asChild
            id="logout-everywhere-dialog"
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <AlertContentShell>
              <AlertDialog.Title asChild>
                <Title as="h2">Log out of all devices?</Title>
              </AlertDialog.Title>
              <AlertDialog.Description asChild>
                <MutedText>You'll need to sign in again on all devices.</MutedText>
              </AlertDialog.Description>
              <VSpace $h={16} />
              <RowBetween>
                <AlertDialog.Cancel asChild>
                  <CancelTextButton type="button">Cancel</CancelTextButton>
                </AlertDialog.Cancel>
                <AlertDialog.Action asChild>
                  <OnboardBtn key={'logout'} onClick={handleConfirmLogoutAll}>
                    Log out
                  </OnboardBtn>
                </AlertDialog.Action>
              </RowBetween>
            </AlertContentShell>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Remove biometric credential confirmation dialog */}
      <AlertDialog.Root open={!!deviceToRemove} onOpenChange={() => setDeviceToRemove(null)}>
        <AlertDialog.Portal container={document.body}>
          <AlertOverlay onClick={() => setDeviceToRemove(null)} />

          <AlertDialog.Content
            asChild
            id="remove-passkey-dialog"
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <AlertContentShell>
              <AlertDialog.Title asChild>
                <Title as="h2">Remove biometric credential?</Title>
              </AlertDialog.Title>
              <VSpace $h={8} />
              <AlertDialog.Description asChild>
                <MutedText>
                  This will remove the biometric credential for {deviceToRemove?.name}. You won't be
                  able to use it for sign-in anymore.
                </MutedText>
              </AlertDialog.Description>
              <VSpace $h={16} />
              <RowBetween>
                <AlertDialog.Cancel asChild>
                  <CancelTextButton type="button">Cancel</CancelTextButton>
                </AlertDialog.Cancel>
                <AlertDialog.Action asChild>
                  <OnboardBtn
                    key={'remove'}
                    onClick={confirmRemovePasskey}
                    style={{ backgroundColor: '#D65F46' }}
                  >
                    Remove
                  </OnboardBtn>
                </AlertDialog.Action>
              </RowBetween>
            </AlertContentShell>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Delete account confirmation dialog */}
      <AlertDialog.Root open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialog.Portal container={document.body}>
          <AlertOverlay onClick={() => setDeleteConfirmOpen(false)} />

          <AlertDialog.Content
            asChild
            id="delete-account-dialog"
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <AlertContentShell>
              <AlertDialog.Title asChild>
                <Title as="h2">Delete your account?</Title>
              </AlertDialog.Title>
              <VSpace $h={8} />
              <AlertDialog.Description asChild>
                <MutedText>
                  This will permanently delete your account and all associated data. This action
                  cannot be undone.
                </MutedText>
              </AlertDialog.Description>
              <VSpace $h={16} />
              <RowBetween>
                <AlertDialog.Cancel asChild>
                  <CancelTextButton type="button" disabled={isDeleting}>
                    Cancel
                  </CancelTextButton>
                </AlertDialog.Cancel>
                <AlertDialog.Action asChild>
                  <OnboardBtn
                    key={'delete'}
                    onClick={handleDeleteAccount}
                    disabled={isDeleting}
                    style={{ backgroundColor: '#D65F46' }}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete account'}
                  </OnboardBtn>
                </AlertDialog.Action>
              </RowBetween>
            </AlertContentShell>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  );
};
